/**
 * usePlayerState · 全局响应式状态（模块级单例，跨组件共享）
 * 用法：任何组件直接 import { state } from '@/composables/usePlayerState'
 *       直接改 state.xxx 就会触发所有用到的组件自动刷新，无需 Pinia
 */
import { reactive, ref, watch } from 'vue'

// ================ 预设系统：内置 + 自定义（localStorage 持久化） ================
const PRESET_KEY = 'sle_presets_v1'
const ACTIVE_KEY = 'sle_active_preset_v1'

/** 内置 3 种预设（不能删除，编辑时另存为自定义）
 *
 * 参数依据：TESOL「精听+泛听」组合策略 + 主流英语教学公开资料
 *  · 泛听：接近原速保持自然语流感，2 遍抓大意不深究细节
 *  · 精听：慢速辨音(<0.5× 失真) → 中速过渡 → 原速达标 → 超速巩固(1.2× 即可)
 *  · 精听总量 7 遍，符合「逐句听写 3-5 遍仍不懂则跳过」的精听上限
 */
export const BUILTIN_PRESETS = [
  {
    id: 'sequence',
    name: '📖 顺序播放',
    builtIn: true,
    desc: '按 1.0× 速度一句接一句播放，快速过完整段内容。',
    adaptive: false,
    lock_one: false,
    pause_gap: 0,
    staircase: [{ rate: 1.0, repeat: 1 }],
  },
  {
    id: 'extensive',
    name: '🎧 泛听模式',
    builtIn: true,
    desc: '每句 0.8× 播放 2 遍，句间停顿 0.5 秒，接近原速保持语流感，用稍慢语速熟悉整体内容，不深究单句。',
    adaptive: true,
    lock_one: false,
    pause_gap: 0.5,
    staircase: [{ rate: 0.8, repeat: 2 }],
  },
  {
    id: 'intensive',
    name: '🔍 精听模式',
    builtIn: true,
    desc: '每句 0.5×2 → 0.75×2 → 1.0×2 → 1.2×1，由慢到快逐档辨音，原速达标后超速巩固，真正听懂为止。句间停顿 1.5 秒，便于跟读。',
    adaptive: true,
    lock_one: false,
    pause_gap: 1.5,
    staircase: [
      { rate: 0.5,  repeat: 2 },
      { rate: 0.75, repeat: 2 },
      { rate: 1.0,  repeat: 2 },
      { rate: 1.2,  repeat: 1 },
    ],
  },
]

function loadPresets() {
  try {
    const raw = localStorage.getItem(PRESET_KEY)
    if (!raw) return [...BUILTIN_PRESETS]
    const customs = JSON.parse(raw) || []
    // 内置 + 自定义合并（内置始终放前面，自定义顺序按保存的）
    return [...BUILTIN_PRESETS, ...customs.filter(p => p && p.id)]
  } catch {
    return [...BUILTIN_PRESETS]
  }
}
function savePresetsToStorage(list) {
  const customs = list.filter(p => !p.builtIn)
  try { localStorage.setItem(PRESET_KEY, JSON.stringify(customs)) } catch {}
}
function loadActiveId() {
  try { return localStorage.getItem(ACTIVE_KEY) || 'sequence' } catch { return 'sequence' }
}
function saveActiveId(id) {
  try { localStorage.setItem(ACTIVE_KEY, id || '') } catch {}
}

/** 全局播放器状态 */
export const state = reactive({
  // ---- 当前课程 & 句子数据 ----
  lesson: null,               // 当前 lesson 元数据
  lessons: [],                // 课程列表（给下拉框用）
  sentences: [],              // [{idx,start,end,en,zh,words}]

  // ---- 播放位置/阶梯 ----
  si: 0,                      // 当前句索引
  stp: 0,                     // 自适应：当前阶梯索引
  rep: 0,                     // 自适应：当前阶梯已经重复次数

  // ---- 模式开关 ----
  adaptive: false,            // 自适应阶梯开/关（默认关，先体验顺序流畅感）
  lock_one: false,            // 锁定单句开/关
  pause_gap: 0,               // 每次播放完成后的停顿时间（秒），0=不停顿
  subMode: 1,                 // 0 关 / 1 EN / 2 中 / 3 双语

  // ---- 阶梯配置（默认取顺序播放 preset 的 staircase） ----
  staircase: [
    { rate: 1.0, repeat: 1 },
  ],

  // ---- 学习模式预设 ----
  presets: loadPresets(),                 // 全部预设（内置 3 + 自定义 N）
  activePresetId: loadActiveId(),         // 当前选中预设 id
})

/** 应用某个预设到 state，重置当前句播放进度（si 不变，只重置阶梯计数器 stp/rep） */
export function applyPreset(id) {
  const p = state.presets.find(x => x.id === id) || state.presets[0]
  if (!p) return
  state.activePresetId = p.id
  saveActiveId(p.id)
  state.adaptive = !!p.adaptive
  state.lock_one = !!p.lock_one
  state.pause_gap = +p.pause_gap || 0
  // 拷贝一份，避免改到原对象
  state.staircase = (p.staircase || []).map(s => ({ rate: +s.rate, repeat: +s.repeat }))
  state.stp = 0
  state.rep = 0
}

/** 新建 / 更新自定义预设；返回新 preset id */
export function upsertPreset(preset) {
  const isNew = !preset.id
  const p = {
    id: preset.id || ('custom_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)),
    name: String(preset.name || '自定义模式').trim().slice(0, 24) || '自定义模式',
    desc: String(preset.desc || '').slice(0, 80),
    builtIn: false,
    adaptive: !!preset.adaptive,
    lock_one: !!preset.lock_one,
    pause_gap: Math.max(0, Math.min(30, +preset.pause_gap || 0)),
    staircase: (preset.staircase || [{ rate: 1.0, repeat: 1 }])
      .filter(s => s && +s.repeat >= 1)
      .map(s => ({ rate: Math.max(0.25, Math.min(3, +s.rate || 1)), repeat: Math.max(1, Math.min(99, +s.repeat | 0)) })),
  }
  const idx = state.presets.findIndex(x => x.id === p.id)
  if (idx >= 0) state.presets.splice(idx, 1, p)
  else state.presets.push(p)
  savePresetsToStorage(state.presets)
  return p.id
}

/** 删除自定义预设；正在使用就自动切到顺序播放 */
export function deletePreset(id) {
  const p = state.presets.find(x => x.id === id)
  if (!p || p.builtIn) return false
  const idx = state.presets.findIndex(x => x.id === id)
  if (idx < 0) return false
  state.presets.splice(idx, 1)
  savePresetsToStorage(state.presets)
  if (state.activePresetId === id) applyPreset('sequence')
  return true
}

/** 克隆一个预设为自定义（编辑内置预设走这个流程） */
export function clonePreset(id, suffix = '副本') {
  const p = state.presets.find(x => x.id === id)
  if (!p) return null
  return upsertPreset({
    name: p.name + ' · ' + suffix,
    desc: p.desc,
    adaptive: p.adaptive,
    lock_one: p.lock_one,
    pause_gap: p.pause_gap,
    staircase: p.staircase.map(s => ({ ...s })),
  })
}

// 启动时：把当前激活预设真正 apply 一次（确保 state.staircase / adaptive 与 preset 对齐）
// 放在 microtask 里，保证 usePlayerState import 完成后立即执行
queueMicrotask(() => {
  const id = state.activePresetId || 'sequence'
  if (state.presets.find(p => p.id === id)) applyPreset(id)
  else applyPreset('sequence')
})


/** Toast 提示（全局可调用） */
const toastEl = ref(null)
let toastTimer = null
export function setToastEl(el) { toastEl.value = el }
export function toast(msg, ms = 1500) {
  const t = toastEl.value
  if (!t) return
  t.textContent = msg
  t.classList.add('show')
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => t.classList.remove('show'), ms)
}

/** 通用 API 封装（/api、/public、/player 通过 vite proxy 或后端同源走） */
export async function api(path, opt) {
  const r = await fetch(path, opt)
  if (!r.ok) {
    const txt = await r.text().catch(() => r.statusText)
    throw new Error(txt || `HTTP ${r.status}`)
  }
  return r.json()
}

/** 秒 → mm:ss */
export function fmtTime(s) {
  s = Math.max(0, s | 0)
  const m = (s / 60) | 0, sec = s % 60
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/** HTML 转义（把句子文本塞进 innerHTML 前必须转） */
export function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}
